
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { to12HourFormat } from '@/utils/formatters';

import { TeacherFormHeader } from '../components/teacher-form/TeacherFormHeader';
import TeacherBasicInfoFields from '../components/teacher-form/TeacherBasicInfoFields';
import TeacherContactInfoFields from '../components/teacher-form/TeacherContactInfoFields';
import TeacherDepartmentFields from '../components/teacher-form/TeacherDepartmentFields';
import { TeacherQualificationFields } from '../components/teacher-form/TeacherQualificationFields';
import { TeacherProfileImageUpload } from '../components/teacher-form/TeacherProfileImageUpload';
import TeacherDocumentsUpload from '../components/teacher-form/TeacherDocumentsUpload';
import TeacherSalaryInfo from '../components/teacher-form/TeacherSalaryInfo';
import TeacherAboutFields from '../components/teacher-form/TeacherAboutFields';
import TeacherSubjectsFields from '../components/teacher-form/TeacherSubjectsFields';
import { TeacherOfflineIndicator } from '@/components/teachers/TeacherOfflineIndicator';

import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useTeacherForm } from '../hooks/useTeacherForm';
import { Helmet } from 'react-helmet-async';

const TeacherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    formData, 
    handleChange, 
    handleDepartmentChange, 
    addDepartment,
    setProfileImage,
    handleImageUpload,
    handleDocumentUpload,
    removeDocument,
    viewDocument,
    saveTeacher 
  } = useTeacherForm();
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'Valid email is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting');
      return;
    }
    
    setIsLoading(true);

    try {
      await saveTeacher();
      toast.success(`Teacher ${id ? 'updated' : 'added'} successfully`);
      navigate('/teachers');
    } catch (error) {
      console.error('Error saving teacher:', error);
      toast.error('Failed to save teacher information');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Helmet>
        <title>{id ? 'Edit' : 'Add'} Teacher - NALC Student Program & Fee Tracker</title>
      </Helmet>
      
      <TeacherOfflineIndicator resource="form" />
      
      <form onSubmit={handleSubmit}>
        <TeacherFormHeader 
          isEditMode={!!id}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <div className="col-span-2 space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
              <Separator className="mb-6" />
              <TeacherBasicInfoFields 
                formData={formData}
                handleChange={handleChange}
                validationErrors={validationErrors}
              />
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
              <Separator className="mb-6" />
              <TeacherContactInfoFields 
                formData={formData}
                handleChange={handleChange}
                validationErrors={validationErrors}
              />
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Department & Subjects</h2>
              <Separator className="mb-6" />
              <TeacherDepartmentFields 
                formData={formData}
                handleDepartmentChange={handleDepartmentChange}
                addDepartment={addDepartment}
              />
            </Card>
            
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Subjects & Expertise</h2>
              <Separator className="mb-6" />
              <TeacherSubjectsFields 
                formData={formData}
                handleChange={handleChange}
              />
            </Card>
            
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">About / Biography</h2>
              <Separator className="mb-6" />
              <TeacherAboutFields 
                formData={formData}
                handleChange={handleChange}
              />
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Qualifications</h2>
              <Separator className="mb-6" />
              <TeacherQualificationFields 
                formData={formData}
                handleChange={handleChange}
              />
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Salary Information</h2>
              <Separator className="mb-6" />
              <TeacherSalaryInfo
                formData={formData}
                handleChange={handleChange}
              />
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Documents</h2>
              <Separator className="mb-6" />
              <TeacherDocumentsUpload
                formData={formData}
                handleDocumentUpload={handleDocumentUpload}
                removeDocument={removeDocument}
                viewDocument={viewDocument}
              />
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Profile Image</h2>
              <Separator className="mb-6" />
              <TeacherProfileImageUpload 
                formData={formData}
                setProfileImage={setProfileImage}
                handleImageUpload={handleImageUpload}
              />
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Preview</h2>
              <Separator className="mb-6" />
              <div className="flex flex-col items-center">
                <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden mb-4">
                  {formData.profileImage && (
                    <img 
                      src={formData.profileImage} 
                      alt="Teacher profile" 
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <h3 className="text-lg font-medium">{formData.firstName} {formData.lastName}</h3>
                <p className="text-gray-500">{formData.position || 'Position not set'}</p>
                <p className="text-gray-500 mt-2">{formData.email}</p>
                <div className="mt-4 w-full space-y-2">
                  <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4 text-gray-500" />
                    <span>{formData.joinDate ? format(new Date(formData.joinDate), 'PPP') : 'Join date not set'}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-500" />
                    <span>{formData.departments?.length || 0} Department(s)</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-4">
          <Button 
            type="button"
            variant="outline"
            onClick={() => navigate('/teachers')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : id ? 'Update Teacher' : 'Add Teacher'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TeacherForm;
